const activeOperations = new Map()
const waiters = new Set()

let sequence = 0
let releaseBlocked = false

function createReleaseError(code, message, extra = {}) {
  const error = new Error(message || code)
  error.code = String(code || '').trim() || 'AGROEL_RELEASE_OPERATION_ERROR'
  Object.assign(error, extra || {})
  return error
}

function notifyWaiters() {
  if (activeOperations.size > 0) return
  for (const resolve of Array.from(waiters)) resolve()
  waiters.clear()
}

function normalizeKind(kind) {
  return String(kind || 'critical-operation').trim() || 'critical-operation'
}

export function isReleaseCriticalOperationBlocked() {
  return releaseBlocked === true || (
    typeof window !== 'undefined' && window.__AGROEL_RELEASE_RESETTING__ === true
  )
}

export function beginReleaseCriticalOperation(kind, {
  abort = null,
  metadata = null
} = {}) {
  if (
    normalizeKind(kind) === 'plc-write' &&
    typeof window !== 'undefined' &&
    window.__AGROEL_PWA_READ_ONLY__ === true
  ) {
    throw createReleaseError(
      'AGROEL_PWA_RELEASE_READ_ONLY',
      'PLC writes are temporarily disabled until the application data update is complete.'
    )
  }
  if (isReleaseCriticalOperationBlocked()) {
    throw createReleaseError(
      'AGROEL_RELEASE_RESET_IN_PROGRESS',
      'Application release reset is in progress.'
    )
  }
  sequence += 1
  const id = `${Date.now()}-${sequence}`
  const operation = {
    id,
    kind: normalizeKind(kind),
    abort: typeof abort === 'function' ? abort : null,
    metadata: metadata && typeof metadata === 'object' ? metadata : null,
    started_at: new Date().toISOString()
  }
  activeOperations.set(id, operation)
  let finished = false
  return {
    id,
    finish() {
      if (finished) return
      finished = true
      activeOperations.delete(id)
      notifyWaiters()
    }
  }
}

export async function withReleaseCriticalOperation(kind, task, options = {}) {
  const handle = beginReleaseCriticalOperation(kind, options)
  try {
    return await task()
  } finally {
    handle.finish()
  }
}

function waitUntilIdle(timeoutMs) {
  if (activeOperations.size === 0) return Promise.resolve(true)
  return new Promise(resolve => {
    let timer = null
    const finish = value => {
      if (timer) clearTimeout(timer)
      waiters.delete(onIdle)
      resolve(value)
    }
    const onIdle = () => finish(true)
    waiters.add(onIdle)
    timer = setTimeout(() => finish(false), Math.max(1, Number(timeoutMs || 0) || 1))
  })
}

export function inspectReleaseCriticalOperations() {
  return Array.from(activeOperations.values()).map(operation => ({
    id: operation.id,
    kind: operation.kind,
    metadata: operation.metadata,
    started_at: operation.started_at,
    abortable: !!operation.abort
  }))
}

export async function prepareReleaseCriticalOperations({ timeoutMs = 10000 } = {}) {
  releaseBlocked = true
  const snapshot = Array.from(activeOperations.values())
  for (const operation of snapshot) {
    if (!operation.abort) continue
    try {
      operation.abort(createReleaseError(
        'AGROEL_RELEASE_PREPARE_ABORT',
        'Operation was cancelled for an application release update.',
        { operationKind: operation.kind }
      ))
    } catch (error) {
      void error
    }
  }
  const idle = await waitUntilIdle(timeoutMs)
  if (idle) {
    return { ok: true, operations: [] }
  }
  const operations = inspectReleaseCriticalOperations()
  releaseBlocked = false
  return {
    ok: false,
    reason: 'release-critical-operations-busy',
    operations
  }
}

export function cancelReleaseCriticalPreparation() {
  releaseBlocked = false
  if (typeof window !== 'undefined') window.__AGROEL_RELEASE_RESETTING__ = false
}
